import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Upload, Wand2, Phone, ArrowRight, Loader2, CheckCircle2, AlertCircle, RotateCcw, Download, Share2, Check } from "lucide-react";
import { SEO, breadcrumbSchema } from "@/components/SEO";
import { SectionHeading, FinalCTA } from "@/components/Shared";
import { DarkHeroAccent } from "@/components/RoofAccents";
import { BUSINESS } from "@/data/content";
import { getBackendUrl } from "@/lib/backend";

const SITE = "https://roofing-goral.com";

// ---------- Material presets ----------
// Tied to the roofing/siding products Goral actually installs. Each preset
// has a stable id matching the backend _MATERIAL_DESCRIPTORS map. `surface`
// controls whether the AI replaces the roof or the exterior siding.
const MATERIALS = [
  // ROOF materials
  { id: "architectural-asphalt", label: "Architectural Asphalt", sub: "GAF Timberline HDZ / CertainTeed Landmark", surface: "roof", popular: true },
  { id: "designer-asphalt", label: "Designer Asphalt", sub: "Slate-look luxury shingle (CertainTeed Presidential)", surface: "roof" },
  { id: "certainteed-landmark-pro", label: "CertainTeed Landmark PRO", sub: "Premium architectural with Max Def color · Lifetime Limited Warranty", surface: "roof", popular: true },
  { id: "certainteed-landmark-pro-climateflex", label: "Landmark PRO ClimateFlex", sub: "Class 4 impact-resistant for Michigan hail · insurance discount eligible", surface: "roof" },
  // SIDING materials
  { id: "certainteed-monogram-siding", label: "CertainTeed Monogram Double 4", sub: "Premium vinyl clapboard · cedar woodgrain · RigidForm 210", surface: "siding", popular: true },
  { id: "certainteed-monogram-siding-d5", label: "CertainTeed Monogram Double 5", sub: "Wider 5-inch clapboard · fewer seams · cedar woodgrain", surface: "siding" },
  { id: "james-hardie-plank-siding", label: "James Hardie HardiePlank", sub: "Fiber cement lap siding · ColorPlus Technology · industry-leading warranty", surface: "siding", popular: true },
  { id: "james-hardie-panel-siding", label: "James Hardie HardiePanel", sub: "Vertical fiber cement · modern board-and-batten look", surface: "siding" },
  { id: "james-hardie-shingle-siding", label: "James Hardie HardieShingle", sub: "Cedar-shingle look fiber cement · perfect for gables & accent walls", surface: "siding" },
];

// ---------- Color palettes ----------
// Curated from actual product catalogs: CertainTeed Landmark / Landmark PRO
// Max Def color line for shingles; CertainTeed Monogram vinyl siding color
// catalog; James Hardie ColorPlus Technology finish palette.
const COLORS_ASPHALT = [
  { label: "Charcoal", hex: "#2C2C2C", popular: true, brand: "GAF Charcoal / CT Moire Black" },
  { label: "Pewter Gray", hex: "#6E6E6E", popular: true, brand: "GAF Pewter Gray" },
  { label: "Weathered Wood", hex: "#5A4E40", popular: true, brand: "GAF Weathered Wood" },
  { label: "Slate", hex: "#3D4451", brand: "GAF Slate / CT Georgetown Gray" },
  { label: "Driftwood", hex: "#7E6F58", brand: "GAF Driftwood" },
  { label: "Barkwood", hex: "#42342A", brand: "GAF Barkwood" },
  { label: "Hickory", hex: "#5A4636", brand: "GAF Hickory" },
  { label: "Shakewood", hex: "#7B6346", brand: "GAF Shakewood" },
  { label: "Mission Brown", hex: "#3F2A1F", brand: "GAF Mission Brown" },
  { label: "Hunter Green", hex: "#1F3D2B", brand: "CT Hunter Green" },
  { label: "Hearthstone Gray", hex: "#7A7268", brand: "OC Duration Hearthstone" },
  { label: "Mojave Tan", hex: "#A38B6B", brand: "GAF Mojave Tan" },
];

// CertainTeed Landmark PRO — Max Def color technology (13 stock + 3 regional).
const COLORS_LANDMARK_PRO = [
  { label: "Max Def Moiré Black", hex: "#1F1F22", popular: true, brand: "CT Landmark PRO" },
  { label: "Max Def Driftwood", hex: "#7C6C55", popular: true, brand: "CT Landmark PRO" },
  { label: "Max Def Weathered Wood", hex: "#584A3B", popular: true, brand: "CT Landmark PRO" },
  { label: "Max Def Georgetown Gray", hex: "#4A5058", popular: true, brand: "CT Landmark PRO" },
  { label: "Max Def Pewter", hex: "#6E6A64", popular: true, brand: "CT Landmark PRO" },
  { label: "Max Def Colonial Slate", hex: "#43464C", popular: true, brand: "CT Landmark PRO" },
  { label: "Max Def Resawn Shake", hex: "#5C4A38", popular: true, brand: "CT Landmark PRO" },
  { label: "Max Def Cobblestone Gray", hex: "#5F5B54", popular: true, brand: "CT Landmark PRO" },
  { label: "Max Def Heather Blend", hex: "#5A4F45", brand: "CT Landmark PRO" },
  { label: "Max Def Charcoal Black", hex: "#252528", brand: "CT Landmark PRO · regional" },
  { label: "Max Def Espresso", hex: "#3A2C22", brand: "CT Landmark PRO" },
  { label: "Max Def Prairie Wood", hex: "#7D6547", brand: "CT Landmark PRO" },
  { label: "Max Def Burnt Sienna", hex: "#8B4A2E", brand: "CT Landmark PRO" },
  { label: "Max Def Shenandoah", hex: "#6A503A", brand: "CT Landmark PRO" },
  { label: "Max Def Red Oak", hex: "#7A3A28", brand: "CT Landmark PRO" },
  { label: "Max Def Coastal Blue", hex: "#3E4E5A", brand: "CT Landmark PRO · designer" },
  { label: "Max Def Evergreen", hex: "#2E4636", brand: "CT Landmark PRO · regional" },
  { label: "Silver Birch", hex: "#9E9A90", brand: "CT Landmark PRO · regional" },
];

// CertainTeed Monogram vinyl siding — actual product catalog (~35 colors).
const COLORS_MONOGRAM_SIDING = [
  { label: "Colonial White", hex: "#F1EEE4", popular: true, brand: "CT Monogram" },
  { label: "Sterling Gray", hex: "#7C8388", popular: true, brand: "CT Monogram" },
  { label: "Charcoal Gray", hex: "#3C3F44", popular: true, brand: "CT Monogram · premium dark" },
  { label: "Autumn Yellow", hex: "#C7A65B", popular: true, brand: "CT Monogram" },
  { label: "Sable Brown", hex: "#5C4635", popular: true, brand: "CT Monogram" },
  { label: "Snow", hex: "#F9F9F5", brand: "CT Monogram" },
  { label: "Natural Clay", hex: "#D9CCA9", brand: "CT Monogram" },
  { label: "Sandstone Beige", hex: "#C9B892", brand: "CT Monogram" },
  { label: "Desert Tan", hex: "#B8A075", brand: "CT Monogram" },
  { label: "Buckskin", hex: "#A48562", brand: "CT Monogram" },
  { label: "Granite Gray", hex: "#5F6469", brand: "CT Monogram" },
  { label: "Flagstone", hex: "#6B6560", brand: "CT Monogram" },
  { label: "Smoky Gray", hex: "#8B8985", brand: "CT Monogram" },
  { label: "Granite Stone", hex: "#4C5054", brand: "CT Monogram" },
  { label: "Savannah Wicker", hex: "#B79879", brand: "CT Monogram" },
  { label: "Herringbone", hex: "#7C6E5E", brand: "CT Monogram" },
  { label: "Cypress", hex: "#6F6449", brand: "CT Monogram" },
  { label: "Spruce", hex: "#3F5A47", brand: "CT Monogram" },
  { label: "Forest", hex: "#2F4636", brand: "CT Monogram" },
  { label: "Moss", hex: "#5C6A47", brand: "CT Monogram" },
  { label: "Seagrass", hex: "#7C8666", brand: "CT Monogram" },
  { label: "Pacific Blue", hex: "#385168", brand: "CT Monogram" },
  { label: "Deep Water Blue", hex: "#2B4358", brand: "CT Monogram" },
  { label: "Deep Mineral", hex: "#3B5563", brand: "CT Monogram" },
  { label: "Oxford Blue", hex: "#293848", brand: "CT Monogram" },
  { label: "Hearthstone", hex: "#8B4A3B", brand: "CT Monogram" },
  { label: "Rustic Clay", hex: "#7C3E2E", brand: "CT Monogram" },
  { label: "Ironstone", hex: "#3D3E42", brand: "CT Monogram · premium dark" },
  { label: "Espresso", hex: "#3A2C22", brand: "CT Monogram · premium dark" },
  { label: "Graphite", hex: "#2C2E31", brand: "CT Monogram · premium dark" },
  { label: "Black Pearl", hex: "#1E1F22", brand: "CT Monogram · premium dark" },
];

// James Hardie ColorPlus Technology — factory-finished palette.
const COLORS_HARDIE_SIDING = [
  { label: "Arctic White", hex: "#F5F4EE", popular: true, brand: "Hardie ColorPlus" },
  { label: "Pearl Gray", hex: "#BEBFB9", popular: true, brand: "Hardie ColorPlus" },
  { label: "Iron Gray", hex: "#4F5257", popular: true, brand: "Hardie ColorPlus · premium dark" },
  { label: "Boothbay Blue", hex: "#4E6B7B", popular: true, brand: "Hardie ColorPlus" },
  { label: "Cobble Stone", hex: "#B8A98F", popular: true, brand: "Hardie ColorPlus" },
  { label: "Navajo Beige", hex: "#D3C4A5", brand: "Hardie ColorPlus" },
  { label: "Light Mist", hex: "#CDCFCC", brand: "Hardie ColorPlus" },
  { label: "Gray Slate", hex: "#5E6266", brand: "Hardie ColorPlus" },
  { label: "Night Gray", hex: "#3B3E42", brand: "Hardie ColorPlus" },
  { label: "Evening Blue", hex: "#3F5468", brand: "Hardie ColorPlus" },
  { label: "Deep Ocean", hex: "#2A3E52", brand: "Hardie ColorPlus" },
  { label: "Khaki Brown", hex: "#8A785C", brand: "Hardie ColorPlus" },
  { label: "Monterey Taupe", hex: "#A99578", brand: "Hardie ColorPlus" },
  { label: "Timber Bark", hex: "#6C5A44", brand: "Hardie ColorPlus" },
  { label: "Mountain Sage", hex: "#7A8770", brand: "Hardie ColorPlus" },
  { label: "Heathered Moss", hex: "#8B8C6F", brand: "Hardie ColorPlus" },
  { label: "Rich Espresso", hex: "#3A2A22", brand: "Hardie ColorPlus · premium dark" },
  { label: "Brownstone", hex: "#7A5D45", brand: "Hardie ColorPlus" },
  { label: "Countrylane Red", hex: "#8E3A2A", brand: "Hardie ColorPlus" },
  { label: "Traditional Red", hex: "#A63A2A", brand: "Hardie ColorPlus" },
  { label: "Midnight Black", hex: "#141519", brand: "Hardie ColorPlus · premium dark" },
];

function colorsForMaterial(materialId) {
  if (materialId === "certainteed-landmark-pro" || materialId === "certainteed-landmark-pro-climateflex") return COLORS_LANDMARK_PRO;
  if (materialId.startsWith("certainteed-monogram")) return COLORS_MONOGRAM_SIDING;
  if (materialId.startsWith("james-hardie")) return COLORS_HARDIE_SIDING;
  return COLORS_ASPHALT;
}

function surfaceForMaterial(materialId) {
  const mat = MATERIALS.find((m) => m.id === materialId);
  return mat?.surface || "roof";
}

// ---------- FAQ for SEO ----------
const FAQS = [
  { q: "How does the Roof & Siding Designer work?", a: "Upload a clear photo of the front of your house, pick a surface (roof or siding), then pick a material and color from the Goral product line. Our AI renders a photo-realistic preview of your home with the new roof or new siding. The system auto-detects the surface — no drawing or masking required." },
  { q: "Can I change both the roof and the siding?", a: "Yes. Pick 'Roof' to render new shingles (CertainTeed Landmark PRO, Landmark PRO ClimateFlex, or architectural/designer asphalt). Pick 'Siding' to render new exterior cladding (CertainTeed Monogram vinyl siding or James Hardie fiber cement in HardiePlank, HardiePanel, or HardieShingle profiles). Everything else in the photo — windows, doors, landscape, sky — stays exactly as it was." },
  { q: "Is the preview free?", a: "Yes — completely free, no signup, no obligation. The preview is for visualization only; for an actual installed quote, click 'Get a real quote' after the render." },
  { q: "How accurate is the visualization?", a: "It's photo-realistic and uses the actual materials we install (CertainTeed Landmark PRO / Landmark PRO ClimateFlex shingles, CertainTeed Monogram vinyl siding, James Hardie fiber cement siding with ColorPlus Technology). Exact color tone on your installed roof or siding may vary slightly from the digital render due to lighting; we provide physical samples on every estimate." },
  { q: "What kind of photo works best?", a: "A clear daytime photo of the front of the house, taken from the curb or driveway, with the roof and siding fully visible. Avoid heavily shaded or rainy-day photos." },
  { q: "Can I try multiple materials and colors?", a: "Yes — just click 'Start over' after a render to try a different combination, or use 'Compare' to see two AI renders side-by-side. Each render is saved if you submit a quote request." },
];

// ---------- Helpers ----------
async function resizeImage(file, maxDim = 1600) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (!blob) return reject(new Error("Image resize failed"));
        resolve(blob);
      }, "image/jpeg", 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image load failed")); };
    img.src = url;
  });
}

// ---------- Composite share image ----------
// Builds a single PNG combining before + after (or two renders) side-by-side
// with clear labels and a subtle Goral watermark. Returns { blob, filename }.
// Backend serves images with CORS (starlette CORSMiddleware with allow_origins
// '*'), so <img crossOrigin="anonymous"> + canvas.toBlob works without taint.
function loadCrossOriginImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load image for share: ${src}`));
    img.src = src;
  });
}

async function buildComparisonImage({ leftUrl, rightUrl, leftLabel, rightLabel, subtitle }) {
  const [leftImg, rightImg] = await Promise.all([
    loadCrossOriginImage(leftUrl),
    loadCrossOriginImage(rightUrl),
  ]);

  // Target: single 1600px wide PNG. Each half = 800px. Extra header/footer.
  const HALF_W = 800;
  const HEADER_H = 92;
  const FOOTER_H = 64;
  const PANEL_W = HALF_W;
  // Compute uniform panel height from the taller of the two images' aspect ratios.
  const leftAspect = leftImg.width / leftImg.height;
  const rightAspect = rightImg.width / rightImg.height;
  const panelAspect = Math.min(leftAspect, rightAspect); // use tighter aspect to keep both filled
  const PANEL_H = Math.round(PANEL_W / panelAspect);
  const TOTAL_W = HALF_W * 2;
  const TOTAL_H = HEADER_H + PANEL_H + FOOTER_H;

  const canvas = document.createElement("canvas");
  canvas.width = TOTAL_W;
  canvas.height = TOTAL_H;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#0F0F10";
  ctx.fillRect(0, 0, TOTAL_W, TOTAL_H);

  // Header title
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 30px 'Inter', 'Segoe UI', sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText("My new roof preview - Goral Construction", 32, HEADER_H / 2 - 8);
  if (subtitle) {
    ctx.fillStyle = "#E7A419"; // brand orange/yellow
    ctx.font = "600 16px 'Inter', sans-serif";
    ctx.fillText(subtitle, 32, HEADER_H / 2 + 20);
  }

  // Draw each panel with object-fit: cover behaviour
  const drawCover = (img, x, y, w, h) => {
    const scale = Math.max(w / img.width, h / img.height);
    const dw = img.width * scale;
    const dh = img.height * scale;
    const dx = x + (w - dw) / 2;
    const dy = y + (h - dh) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.restore();
  };
  drawCover(leftImg,  0,      HEADER_H, PANEL_W, PANEL_H);
  drawCover(rightImg, PANEL_W, HEADER_H, PANEL_W, PANEL_H);

  // Divider line between panels
  ctx.strokeStyle = "#E7A419";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(PANEL_W, HEADER_H);
  ctx.lineTo(PANEL_W, HEADER_H + PANEL_H);
  ctx.stroke();

  // Panel labels — dark chip in top-left of each panel
  const drawLabel = (text, x, y, accent) => {
    ctx.font = "800 18px 'Inter', sans-serif";
    ctx.textBaseline = "middle";
    const paddingX = 14;
    const paddingY = 10;
    const metrics = ctx.measureText(text);
    const w = metrics.width + paddingX * 2;
    const h = 36;
    ctx.fillStyle = accent ? "#E7A419" : "rgba(15,15,16,0.9)";
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "left";
    ctx.fillText(text, x + paddingX, y + h / 2);
  };
  drawLabel(leftLabel,  20,          HEADER_H + 20, false);
  drawLabel(rightLabel, PANEL_W + 20, HEADER_H + 20, true);

  // Footer branding
  ctx.fillStyle = "#0F0F10";
  ctx.fillRect(0, HEADER_H + PANEL_H, TOTAL_W, FOOTER_H);
  ctx.strokeStyle = "#E7A419";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(0, HEADER_H + PANEL_H);
  ctx.lineTo(TOTAL_W, HEADER_H + PANEL_H);
  ctx.stroke();

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 18px 'Inter', sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText("roofing-goral.com", 32, HEADER_H + PANEL_H + FOOTER_H / 2);
  ctx.textAlign = "right";
  ctx.fillStyle = "#E7A419";
  ctx.font = "700 18px 'Inter', sans-serif";
  ctx.fillText("Free AI Roof & Siding Designer  ·  (248) 804-6933", TOTAL_W - 32, HEADER_H + PANEL_H + FOOTER_H / 2);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("Could not build share image"));
      resolve(blob);
    }, "image/png", 0.95);
  });
}

// ---------- Page component ----------
export default function RoofDesignerPage() {
  const [stage, setStage] = useState("upload"); // upload | choose | rendering | result | lead | done
  const [originalDataUrl, setOriginalDataUrl] = useState(null);
  const [originalBase64, setOriginalBase64] = useState(null);
  // Surface tab: "roof" / "siding" / "combo".
  //   "roof"   → uses `material` + `color` (single-surface roof).
  //   "siding" → uses `material` + `color` (single-surface siding).
  //   "combo"  → uses `roofMaterial` + `roofColor` AND `sidingMaterial` +
  //              `sidingColor` (both surfaces rendered in one AI pass).
  const [surfaceTab, setSurfaceTab] = useState("roof");
  const [material, setMaterial] = useState(MATERIALS[0]);
  const [colorList, setColorList] = useState(COLORS_ASPHALT);
  const [color, setColor] = useState(COLORS_ASPHALT[0]);
  // Combo-mode selections — kept independent from single-surface state so
  // switching tabs preserves what the homeowner already picked.
  const [roofMaterial, setRoofMaterial] = useState(MATERIALS.find(m => m.surface === "roof") || MATERIALS[0]);
  const [roofColor, setRoofColor] = useState(colorsForMaterial((MATERIALS.find(m => m.surface === "roof") || MATERIALS[0]).id)[0]);
  const [sidingMaterial, setSidingMaterial] = useState(MATERIALS.find(m => m.surface === "siding") || MATERIALS[0]);
  const [sidingColor, setSidingColor] = useState(colorsForMaterial((MATERIALS.find(m => m.surface === "siding") || MATERIALS[0]).id)[0]);
  const [customNotes, setCustomNotes] = useState("");
  const [result, setResult] = useState(null); // { render_id, original_url, rendered_url, material_id, material_label, color_label, color_hex }
  const [gallery, setGallery] = useState([]); // array of previously generated renders for the current photo
  const [error, setError] = useState(null);
  const [sliderPos, setSliderPos] = useState(50);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", notes: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  // Compare mode: when truthy, the slider shows `compareWith` on the left half
  // and `result` on the right half (two AI renders side-by-side). When null,
  // the slider shows the original photo (before) vs `result` (after).
  const [compareWith, setCompareWith] = useState(null);
  // Share flow state: "idle" | "building" | "shared" | "downloaded" | "error".
  // Used to show inline feedback on the Share button without a toast library.
  const [shareState, setShareState] = useState("idle");
  // Live countdown timer state during /api/visualizer/render. Keeps the user
  // engaged on the page so they don't bounce while Gemini works.
  const [elapsed, setElapsed] = useState(0);
  const elapsedTimer = useRef(null);
  const fileInputRef = useRef(null);

  // Whenever material changes, swap the color palette and reset to its first entry.
  useEffect(() => {
    const list = colorsForMaterial(material.id);
    setColorList(list);
    setColor(list[0]);
  }, [material]);

  // Run the elapsed-time counter only during the "rendering" stage.
  useEffect(() => {
    if (stage !== "rendering") {
      if (elapsedTimer.current) { clearInterval(elapsedTimer.current); elapsedTimer.current = null; }
      setElapsed(0);
      return;
    }
    setElapsed(0);
    elapsedTimer.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { if (elapsedTimer.current) clearInterval(elapsedTimer.current); };
  }, [stage]);

  async function handleFile(file) {
    setError(null);
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
      setError("Please upload a JPG, PNG, or WEBP image.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError("Image is too large (15 MB max). Try a smaller photo.");
      return;
    }
    try {
      const resized = await resizeImage(file, 1600);
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(resized);
      });
      const b64 = dataUrl.split(",")[1];
      setOriginalDataUrl(dataUrl);
      setOriginalBase64(b64);
      setGallery([]); // new photo = fresh gallery
      setStage("choose");
    } catch (e) {
      setError("Could not read that image. Try a JPG or PNG photo.");
    }
  }

  // Unique cache key for a (material, color) combo on the current uploaded photo.
  // Combo mode uses a compound key that includes BOTH roof and siding selections
  // so switching between combo variants correctly hits/misses cache.
  const comboKey = (matId, colorLabel) => `${matId}__${colorLabel}`;
  const currentCacheKey = () =>
    surfaceTab === "combo"
      ? `combo__${roofMaterial.id}_${roofColor.label}__${sidingMaterial.id}_${sidingColor.label}`
      : comboKey(material.id, color.label);

  async function handleGenerate() {
    if (!originalBase64) return;
    // Cache hit -> instant switch, skip the LLM call entirely.
    const activeKey = currentCacheKey();
    const cached = gallery.find((g) => g.key === activeKey);
    if (cached) {
      setResult(cached);
      setStage("result");
      return;
    }
    setStage("rendering");
    setError(null);
    setResult(null);
    try {
      const apiUrl = `${getBackendUrl()}/api/visualizer/render`;
      const resp = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          surfaceTab === "combo"
            ? {
                image_b64: originalBase64,
                surface: "combo",
                roof_material: roofMaterial.id,
                roof_material_label: `${roofMaterial.label} - ${roofMaterial.sub}`,
                roof_color: roofColor.label,
                roof_color_hex: roofColor.hex,
                siding_material: sidingMaterial.id,
                siding_material_label: `${sidingMaterial.label} - ${sidingMaterial.sub}`,
                siding_color: sidingColor.label,
                siding_color_hex: sidingColor.hex,
                material: "",     // required by schema but ignored in combo
                color: "",        // required by schema but ignored in combo
                custom_notes: customNotes.trim(),
              }
            : {
                image_b64: originalBase64,
                material: material.id,
                material_label: `${material.label} - ${material.sub}`,
                color: color.label,
                color_hex: color.hex,
                custom_notes: customNotes.trim(),
                surface: material.surface || "roof",
              }
        ),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || data.message || `Render failed (${resp.status})`);
      }
      const enriched = surfaceTab === "combo"
        ? {
            ...data,
            key: activeKey,
            material_id: `combo__${roofMaterial.id}+${sidingMaterial.id}`,
            material_label: `${roofMaterial.label} roof · ${sidingMaterial.label} siding`,
            color_label: `${roofColor.label} · ${sidingColor.label}`,
            color_hex: roofColor.hex,
          }
        : {
            ...data,
            key: activeKey,
            material_id: material.id,
            material_label: material.label,
            color_label: color.label,
            color_hex: color.hex,
          };
      setResult(enriched);
      // Append to gallery (cap at 6 to keep memory + UI sane).
      setGallery((g) => {
        const existing = g.filter((x) => x.key !== enriched.key);
        const next = [...existing, enriched];
        return next.slice(-6);
      });
      setStage("result");
    } catch (e) {
      console.error(e);
      setError(e.message || "Something went wrong with the AI render. Please try again.");
      setStage("choose");
    }
  }

  // Switch to a previously generated render from the gallery without calling the API.
  function pickFromGallery(item) {
    const mat = MATERIALS.find((m) => m.id === item.material_id) || MATERIALS[0];
    setMaterial(mat);
    // Find the matching color object (so the next "Try another" returns to the right palette).
    const list = colorsForMaterial(mat.id);
    const c = list.find((x) => x.label === item.color_label) || list[0];
    setColor(c);
    setResult(item);
    setCompareWith(null); // exit compare mode when picking single design
    setStage("result");
  }

  // Pin a second render to compare against the current `result` in the slider.
  // If the user clicks the same item that is already the active result, ignore.
  function pickCompare(item) {
    if (!result || item.key === result.key) return;
    setCompareWith(item);
  }

  async function handleLeadSubmit(ev) {
    ev.preventDefault();
    if (!result) return;
    setLeadSubmitting(true);
    setError(null);
    try {
      const apiUrl = `${getBackendUrl()}/api/visualizer/lead`;
      const resp = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          render_id: result.render_id,
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          notes: lead.notes ? `${lead.notes}\n` : "",
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.detail || "Lead submission failed");
      setStage("done");
    } catch (e) {
      setError(e.message || "Could not submit your request. Please try again or call us.");
    } finally {
      setLeadSubmitting(false);
    }
  }

  function resetAll() {
    setStage("upload");
    setOriginalDataUrl(null);
    setOriginalBase64(null);
    setResult(null);
    setCompareWith(null);
    setGallery([]);
    setError(null);
    setMaterial(MATERIALS[0]);
    setCustomNotes("");
    setLead({ name: "", email: "", phone: "", notes: "" });
    setShareState("idle");
  }

  // Build a side-by-side comparison PNG and share it via Web Share API
  // (mobile: WhatsApp / Messenger / SMS / IG DM) with a fallback to download.
  async function handleShare() {
    if (!result) return;
    setShareState("building");
    try {
      const backend = getBackendUrl();
      const leftUrl  = compareWith ? `${backend}${compareWith.rendered_url}` : `${backend}${result.original_url}`;
      const rightUrl = `${backend}${result.rendered_url}`;
      const leftLabel  = compareWith ? `${compareWith.material_label} · ${compareWith.color_label}` : "BEFORE";
      const rightLabel = compareWith ? `${result.material_label} · ${result.color_label}` : `AFTER · ${result.material_label} in ${result.color_label}`;
      const subtitle = compareWith
        ? "Side-by-side AI comparison"
        : `${result.material_label} in ${result.color_label}`;

      const blob = await buildComparisonImage({ leftUrl, rightUrl, leftLabel, rightLabel, subtitle });
      const filename = `goral-roof-preview-${Date.now()}.png`;
      const file = new File([blob], filename, { type: "image/png" });

      // Try native share (mobile). Feature-detect files support so Chrome
      // desktop doesn't throw on unsupported files array.
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({
            files: [file],
            title: "My new roof preview - Goral Construction",
            text: "Check out how my house would look with a new roof! Made with Goral's free AI Roof Designer.",
          });
          setShareState("shared");
        } catch (shareErr) {
          // User cancelled share or share failed; silently fall back to download.
          if (shareErr?.name === "AbortError") { setShareState("idle"); return; }
          triggerDownload(blob, filename);
          setShareState("downloaded");
        }
      } else {
        // Desktop / unsupported → download PNG so user can attach it anywhere.
        triggerDownload(blob, filename);
        setShareState("downloaded");
      }
      setTimeout(() => setShareState("idle"), 3500);
    } catch (e) {
      console.error("Share failed:", e);
      setShareState("error");
      setError("Could not build the share image. Try downloading the render instead.");
      setTimeout(() => setShareState("idle"), 3500);
    }
  }

  function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  const renderedFullUrl = result?.rendered_url ? `${getBackendUrl()}${result.rendered_url}` : null;
  const originalFullUrl = result?.original_url ? `${getBackendUrl()}${result.original_url}` : null;

  return (
    <div>
      <SEO
        title="Roof Designer - Preview Your New Roof in 30 Seconds | Goral Construction"
        description="Upload a photo of your house, pick a roofing material and color, and our AI renders a photo-realistic preview of your new roof. Free, no signup, Oakland County MI."
        path="/roof-designer"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              name: "Goral Roof Designer",
              applicationCategory: "DesignApplication",
              operatingSystem: "Web",
              offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
              provider: {
                "@type": "RoofingContractor",
                "@id": `${SITE}/#business`,
                name: "Goral Construction LLC",
                url: `${SITE}/`,
                telephone: "(248) 804-6933",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "4192 Farner Ave",
                  addressLocality: "Waterford Township",
                  addressRegion: "MI",
                  postalCode: "48329",
                  addressCountry: "US",
                },
              },
              description: "Free AI-powered roof visualization tool for Oakland County, MI homeowners. Upload a photo, pick a material and color, see a photo-realistic render in 30 seconds.",
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            breadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "Roof Designer", path: "/roof-designer" },
            ]),
          ],
        }}
      />

      {/* HERO */}
      <section className="bg-bg-dark text-white py-20 md:py-24 relative overflow-hidden">
        <DarkHeroAccent tone="house" />
        <div className="container-x relative">
          <div className="overline text-brand inline-flex items-center gap-2"><Sparkles className="w-4 h-4" /> Free AI tool · Oakland County, MI</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tight mt-4 leading-[1.05] max-w-3xl">
            See your new roof <span className="text-brand">before you buy it.</span>
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            Upload a photo of your house, pick a roofing material and color from our actual product line, and our AI renders a photo-realistic preview in 30 seconds. Free, no signup.
          </p>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="py-12 md:py-20">
        <div className="container-x">
          {error && (
            <div className="mb-8 bg-red-50 border-l-4 border-red-500 text-red-900 p-4 flex items-start gap-3 max-w-4xl mx-auto" role="alert" data-testid="designer-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div><strong className="font-bold">{error}</strong></div>
            </div>
          )}

          {/* STAGE: Upload */}
          {stage === "upload" && (
            <div className="max-w-3xl mx-auto" data-testid="designer-upload">
              <SectionHeading overline="Step 1 of 3" title="Upload a photo of the front of your house." align="center" />
              <p className="text-ink-soft mt-4 max-w-xl mx-auto text-center leading-relaxed">A clear daytime photo from the curb works best. Roof fully visible, no heavy shade. JPG or PNG up to 15 MB.</p>
              <label
                htmlFor="rd-file"
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-brand"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("ring-brand"); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("ring-brand");
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleFile(f);
                }}
                className="mt-10 block border-2 border-dashed border-ink-line ring-2 ring-transparent transition-all hover:border-brand hover:bg-brand/5 cursor-pointer p-12 md:p-20 text-center"
                data-testid="designer-dropzone"
              >
                <Upload className="w-12 h-12 mx-auto text-brand" />
                <div className="font-heading text-xl md:text-2xl font-extrabold mt-4">Drop a photo here or click to choose</div>
                <div className="text-ink-soft text-sm mt-2">JPG, PNG, or WEBP · max 15 MB</div>
              </label>
              <input
                ref={fileInputRef}
                id="rd-file"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                data-testid="designer-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          )}

          {/* STAGE: Choose material + color */}
          {stage === "choose" && originalDataUrl && (
            <div className="grid lg:grid-cols-[1fr_1fr] gap-10 max-w-6xl mx-auto" data-testid="designer-choose">
              {/* Left: photo */}
              <div>
                <SectionHeading overline="Step 2 of 3" title="Pick a material and color." />
                <p className="text-ink-soft mt-3 leading-relaxed">Our AI will automatically locate the {surfaceTab === "siding" ? "exterior siding" : "roof"} in your photo. Everything else stays exactly as it is — windows, doors, landscape, sky.</p>
                <div className="mt-6 aspect-[4/3] overflow-hidden bg-bg-soft border border-ink-line">
                  <img src={originalDataUrl} alt="Your house" className="w-full h-full object-cover" />
                </div>
                <button onClick={resetAll} className="mt-4 text-sm text-brand font-semibold hover:underline inline-flex items-center gap-1" data-testid="designer-change-photo">
                  <RotateCcw className="w-4 h-4" /> Use a different photo
                </button>
              </div>

              {/* Right: pickers */}
              <div>
                {/* Surface tab toggle — Roof / Siding / Combo (both). */}
                <div className="overline text-brand mb-3">What are we replacing?</div>
                <div className="grid grid-cols-3 gap-2 mb-6" role="tablist" data-testid="designer-surface-tabs">
                  {[
                    { id: "roof",   label: "Roof",    sub: "Shingles only" },
                    { id: "siding", label: "Siding",  sub: "Wall cladding only" },
                    { id: "combo",  label: "Both",    sub: "Roof + siding · combo" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      role="tab"
                      aria-selected={surfaceTab === tab.id}
                      onClick={() => {
                        setSurfaceTab(tab.id);
                        if (tab.id !== "combo") {
                          const firstOfSurface = MATERIALS.find((m) => m.surface === tab.id);
                          if (firstOfSurface) setMaterial(firstOfSurface);
                        }
                      }}
                      data-testid={`designer-surface-tab-${tab.id}`}
                      className={`p-3 border-2 text-left transition-all ${surfaceTab === tab.id ? "border-brand bg-brand/5" : "border-ink-line hover:border-ink"} ${tab.id === "combo" ? "relative" : ""}`}
                    >
                      {tab.id === "combo" && <div className="absolute -top-2 -right-2 bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 tracking-wider">NEW</div>}
                      <div className="font-heading text-base font-extrabold">{tab.label}</div>
                      <div className="text-[11px] text-ink-soft leading-tight mt-0.5">{tab.sub}</div>
                    </button>
                  ))}
                </div>

                {surfaceTab !== "combo" ? (
                  // Single-surface picker (roof OR siding)
                  <div>
                    <div className="overline text-brand mb-3">Material</div>
                    <div className="grid grid-cols-2 gap-2.5" data-testid="designer-material-list">
                      {MATERIALS.filter((m) => m.surface === surfaceTab).map((m) => (
                        <button
                          key={m.id}
                          onClick={() => setMaterial(m)}
                          data-testid={`designer-material-${m.id}`}
                          className={`relative text-left p-3 border-2 transition-all ${material.id === m.id ? "border-brand bg-brand/5" : "border-ink-line hover:border-ink"}`}
                        >
                          {m.popular && <div className="absolute -top-2 -right-2 bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 tracking-wider">POPULAR</div>}
                          <div className="font-heading text-sm font-extrabold">{m.label}</div>
                          <div className="text-[11px] text-ink-soft leading-tight mt-1">{m.sub}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Combo picker — two independent selections in one render
                  <div className="space-y-8" data-testid="designer-combo-pickers">
                    {/* Roof half */}
                    <div>
                      <div className="overline text-brand mb-2">1 · Roof material</div>
                      <div className="grid grid-cols-2 gap-2" data-testid="designer-combo-roof-list">
                        {MATERIALS.filter((m) => m.surface === "roof").map((m) => (
                          <button
                            key={m.id}
                            onClick={() => {
                              setRoofMaterial(m);
                              setRoofColor(colorsForMaterial(m.id)[0]);
                            }}
                            data-testid={`designer-combo-roof-${m.id}`}
                            className={`text-left p-2.5 border-2 transition-all ${roofMaterial.id === m.id ? "border-brand bg-brand/5" : "border-ink-line hover:border-ink"}`}
                          >
                            <div className="font-heading text-xs font-extrabold leading-tight">{m.label}</div>
                          </button>
                        ))}
                      </div>
                      <div className="overline text-brand mt-4 mb-2">Roof color</div>
                      <div className="grid grid-cols-6 gap-1.5" data-testid="designer-combo-roof-colors">
                        {colorsForMaterial(roofMaterial.id).slice(0, 12).map((c) => (
                          <button
                            key={c.label}
                            onClick={() => setRoofColor(c)}
                            title={c.label}
                            style={{ backgroundColor: c.hex }}
                            className={`w-full aspect-square border-2 rounded transition-all ${roofColor.label === c.label ? "border-brand ring-2 ring-brand/40" : "border-ink-line hover:border-ink"}`}
                            data-testid={`designer-combo-roof-color-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          />
                        ))}
                      </div>
                      <div className="text-xs text-ink-soft mt-1.5">
                        <span className="font-semibold">{roofMaterial.label}</span> · <span>{roofColor.label}</span>
                      </div>
                    </div>

                    {/* Siding half */}
                    <div>
                      <div className="overline text-brand mb-2">2 · Siding material</div>
                      <div className="grid grid-cols-2 gap-2" data-testid="designer-combo-siding-list">
                        {MATERIALS.filter((m) => m.surface === "siding").map((m) => (
                          <button
                            key={m.id}
                            onClick={() => {
                              setSidingMaterial(m);
                              setSidingColor(colorsForMaterial(m.id)[0]);
                            }}
                            data-testid={`designer-combo-siding-${m.id}`}
                            className={`text-left p-2.5 border-2 transition-all ${sidingMaterial.id === m.id ? "border-brand bg-brand/5" : "border-ink-line hover:border-ink"}`}
                          >
                            <div className="font-heading text-xs font-extrabold leading-tight">{m.label}</div>
                          </button>
                        ))}
                      </div>
                      <div className="overline text-brand mt-4 mb-2">Siding color</div>
                      <div className="grid grid-cols-6 gap-1.5" data-testid="designer-combo-siding-colors">
                        {colorsForMaterial(sidingMaterial.id).slice(0, 12).map((c) => (
                          <button
                            key={c.label}
                            onClick={() => setSidingColor(c)}
                            title={c.label}
                            style={{ backgroundColor: c.hex }}
                            className={`w-full aspect-square border-2 rounded transition-all ${sidingColor.label === c.label ? "border-brand ring-2 ring-brand/40" : "border-ink-line hover:border-ink"}`}
                            data-testid={`designer-combo-siding-color-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          />
                        ))}
                      </div>
                      <div className="text-xs text-ink-soft mt-1.5">
                        <span className="font-semibold">{sidingMaterial.label}</span> · <span>{sidingColor.label}</span>
                      </div>
                    </div>
                  </div>
                )}

                {surfaceTab !== "combo" && (
                  <div className="mt-7">
                    <div className="overline text-brand mb-3">Color · stocked at Michigan suppliers</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="designer-color-list">
                      {colorList.map((c) => (
                        <button
                          key={c.label}
                          onClick={() => setColor(c)}
                          data-testid={`designer-color-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`relative text-left p-2 border-2 transition-all ${color.label === c.label ? "border-brand" : "border-ink-line hover:border-ink"}`}
                          title={c.brand || c.label}
                        >
                          <div className="w-full aspect-square mb-2 border border-ink-line/40" style={{ background: c.hex }} />
                          <div className="text-[11px] font-heading font-bold text-ink leading-tight">{c.label}</div>
                          {c.popular && <div className="text-[9px] text-brand font-bold uppercase tracking-wider mt-0.5">Popular</div>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-7">
                  <label htmlFor="rd-notes" className="overline text-brand mb-2 block">Optional notes for the AI</label>
                  <textarea
                    id="rd-notes"
                    value={customNotes}
                    onChange={(e) => setCustomNotes(e.target.value)}
                    placeholder="e.g., also paint the gutters white, add a ridge vent strip..."
                    rows={2}
                    maxLength={300}
                    className="w-full border border-ink-line p-3 text-sm focus:border-brand outline-none"
                    data-testid="designer-notes"
                  />
                </div>

                <button
                  onClick={handleGenerate}
                  className="mt-8 btn-primary text-base w-full justify-center"
                  data-testid="designer-generate"
                >
                  {(() => {
                    if (surfaceTab === "combo") {
                      const key = `combo__${roofMaterial.id}_${roofColor.label}__${sidingMaterial.id}_${sidingColor.label}`;
                      if (gallery.find((g) => g.key === key)) {
                        return <><Sparkles className="w-4 h-4" /> Switch to this combo instantly</>;
                      }
                      return <><Wand2 className="w-4 h-4" /> Generate roof + siding preview</>;
                    }
                    const noun = material.surface === "siding" ? "siding" : "roof";
                    if (gallery.find((g) => g.key === comboKey(material.id, color.label))) {
                      return <><Sparkles className="w-4 h-4" /> Switch to this design instantly</>;
                    }
                    return <><Wand2 className="w-4 h-4" /> Generate my new {noun}</>;
                  })()}
                </button>
                <div className="text-xs text-ink-soft text-center mt-2">
                  {gallery.length > 0
                    ? `Free · ${gallery.length} design${gallery.length === 1 ? "" : "s"} saved · pick any from the result gallery`
                    : "Free · ~30 second render · No signup"}
                </div>
              </div>
            </div>
          )}

          {/* STAGE: Rendering - keeps the user engaged with a live counter,
              progress bar, and rotating engagement copy so they don't bounce. */}
          {stage === "rendering" && (
            <RenderingStage elapsed={elapsed} material={material} color={color} />
          )}

          {/* STAGE: Result */}
          {stage === "result" && result && (
            <div className="max-w-5xl mx-auto" data-testid="designer-result">
              <SectionHeading
                overline={compareWith ? "Side by side" : "Step 3 of 3"}
                title={compareWith ? "Compare two designs." : "Drag the slider to compare."}
                align="center"
              />
              <p className="text-ink-soft mt-3 text-center max-w-xl mx-auto">
                {compareWith
                  ? <>Left: <strong className="text-ink">{compareWith.material_label} · {compareWith.color_label}</strong> · Right: <strong className="text-ink">{result.material_label} · {result.color_label}</strong></>
                  : <>Before (left) and after (right). Like what you see? Get a real installed quote from Maciej.</>}
              </p>

              <div className="mt-8 relative aspect-[4/3] overflow-hidden bg-bg-soft border border-ink-line select-none" data-testid="designer-slider">
                {/* Left half: either the original photo (default) or another render (compare mode). */}
                <img
                  src={compareWith ? `${getBackendUrl()}${compareWith.rendered_url}` : originalFullUrl}
                  alt={compareWith ? `${compareWith.material_label} in ${compareWith.color_label}` : "Original roof"}
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                />
                <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }}>
                  <img src={renderedFullUrl} alt={`${result.material_label} in ${result.color_label}`} className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="absolute top-0 bottom-0 w-1 bg-brand pointer-events-none" style={{ left: `${sliderPos}%` }}>
                  <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center font-bold shadow-2xl">⇆</div>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={sliderPos}
                  onChange={(e) => setSliderPos(Number(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
                  data-testid="designer-slider-range"
                  aria-label={compareWith ? "Compare two designs slider" : "Before/after slider"}
                />
                {/* Labels - adapt based on mode */}
                {compareWith ? (
                  <>
                    <div className="absolute top-4 left-4 bg-ink/85 text-white text-[10px] font-bold px-2 py-1 tracking-wider pointer-events-none max-w-[40%]">
                      <span className="block opacity-70">LEFT</span>
                      <span className="block truncate">{compareWith.color_label}</span>
                    </div>
                    <div className="absolute top-4 right-4 bg-brand/95 text-white text-[10px] font-bold px-2 py-1 tracking-wider pointer-events-none max-w-[40%] text-right">
                      <span className="block opacity-80">RIGHT</span>
                      <span className="block truncate">{result.color_label}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="absolute top-4 left-4 bg-ink text-white text-xs font-bold px-2 py-1 tracking-wider pointer-events-none">BEFORE</div>
                    <div className="absolute top-4 right-4 bg-brand text-white text-xs font-bold px-2 py-1 tracking-wider pointer-events-none">AFTER</div>
                  </>
                )}
              </div>

              {/* Compare mode controls */}
              {compareWith && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => setCompareWith(null)}
                    className="text-sm font-bold text-brand hover:underline inline-flex items-center gap-1.5"
                    data-testid="designer-exit-compare"
                  >
                    <RotateCcw className="w-4 h-4" /> Back to before/after view
                  </button>
                </div>
              )}

              {/* Gallery: thumbnails of every render generated for the current photo.
                  Single click = switch the active design. "Compare" button on each
                  non-active thumb pins it as the left side of the slider. */}
              {gallery.length > 1 && (
                <div className="mt-8" data-testid="designer-gallery">
                  <div className="overline text-brand mb-3 text-center">
                    {compareWith ? "Pick any to swap right side · use Compare to swap left side" : "Click any to switch · or use Compare to view two designs side-by-side"}
                  </div>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {gallery.map((g) => {
                      const isActive = result.key === g.key;
                      const isCompare = compareWith && compareWith.key === g.key;
                      const fullUrl = `${getBackendUrl()}${g.rendered_url}`;
                      return (
                        <div
                          key={g.key}
                          className={`group relative border-2 transition-all ${isActive ? "border-brand ring-2 ring-brand/40" : isCompare ? "border-ink ring-2 ring-ink/30" : "border-ink-line hover:border-ink"}`}
                          title={`${g.material_label} · ${g.color_label}`}
                        >
                          <button
                            type="button"
                            onClick={() => pickFromGallery(g)}
                            data-testid={`designer-gallery-${g.key}`}
                            className="block w-full text-left"
                          >
                            <img src={fullUrl} alt={`${g.material_label} in ${g.color_label}`} className="w-28 h-20 sm:w-32 sm:h-24 object-cover" />
                            <div className="px-2 py-1.5 bg-white text-left">
                              <div className="text-[10px] font-heading font-bold text-ink leading-tight truncate max-w-[7rem]">{g.material_label}</div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="w-3 h-3 border border-ink-line/40" style={{ background: g.color_hex }} />
                                <span className="text-[10px] text-ink-soft truncate">{g.color_label}</span>
                              </div>
                            </div>
                          </button>
                          {!isActive && !isCompare && (
                            <button
                              type="button"
                              onClick={() => pickCompare(g)}
                              data-testid={`designer-compare-${g.key}`}
                              className="absolute bottom-0 right-0 m-1 bg-white/95 backdrop-blur text-ink text-[10px] font-extrabold tracking-wide uppercase px-1.5 py-1 hover:bg-ink hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Pin this on the left side of the slider"
                            >
                              VS
                            </button>
                          )}
                          {isActive && <div className="absolute top-1 right-1 bg-brand text-white text-[9px] font-bold px-1 py-0.5 tracking-wider">ACTIVE</div>}
                          {isCompare && <div className="absolute top-1 right-1 bg-ink text-white text-[9px] font-bold px-1 py-0.5 tracking-wider">VS</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-center text-xs text-ink-soft mt-3">
                    {gallery.length} of 6 max · hover any thumbnail and tap <span className="font-bold text-ink">VS</span> to compare two designs side-by-side
                  </div>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <button onClick={() => setStage("lead")} className="btn-primary" data-testid="designer-get-quote">
                  Get a real quote for this roof <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => { setCompareWith(null); setStage("choose"); }} className="btn-secondary" data-testid="designer-try-another">
                  <RotateCcw className="w-4 h-4" /> Try another combination
                </button>
                <a href={renderedFullUrl} download className="btn-secondary" data-testid="designer-download">
                  <Download className="w-4 h-4" /> Download render
                </a>
                <button
                  onClick={handleShare}
                  disabled={shareState === "building"}
                  className="btn-secondary"
                  data-testid="designer-share"
                  aria-label="Share side-by-side comparison"
                >
                  {shareState === "building" && <><Loader2 className="w-4 h-4 animate-spin" /> Preparing...</>}
                  {shareState === "shared" && <><Check className="w-4 h-4" /> Shared!</>}
                  {shareState === "downloaded" && <><Check className="w-4 h-4" /> Image saved</>}
                  {shareState === "error" && <><AlertCircle className="w-4 h-4" /> Try again</>}
                  {shareState === "idle" && <><Share2 className="w-4 h-4" /> Share comparison</>}
                </button>
              </div>
              <div className="text-center text-sm text-ink-soft mt-4">Material: <strong className="text-ink">{material.label}</strong> · Color: <strong className="text-ink">{color.label}</strong></div>
            </div>
          )}

          {/* STAGE: Lead form */}
          {stage === "lead" && result && (
            <div className="max-w-2xl mx-auto" data-testid="designer-lead-stage">
              <SectionHeading overline="Almost done" title="Get a real installed quote." align="center" />
              <p className="text-ink-soft mt-3 text-center leading-relaxed">Maciej Ogonowski personally reviews every Roof Designer lead. Expect a callback within 1 business day.</p>
              <form onSubmit={handleLeadSubmit} className="mt-8 space-y-4" data-testid="designer-lead-form">
                <input required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Full name *" className="w-full border border-ink-line p-3.5 focus:border-brand outline-none" data-testid="designer-lead-name" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email *" className="border border-ink-line p-3.5 focus:border-brand outline-none" data-testid="designer-lead-email" />
                  <input required type="tel" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Phone *" className="border border-ink-line p-3.5 focus:border-brand outline-none" data-testid="designer-lead-phone" />
                </div>
                <textarea value={lead.notes} onChange={(e) => setLead({ ...lead, notes: e.target.value })} placeholder="Optional - address, timing, questions..." rows={3} className="w-full border border-ink-line p-3.5 focus:border-brand outline-none" data-testid="designer-lead-notes" />
                <div className="flex flex-wrap gap-3">
                  <button disabled={leadSubmitting} className="btn-primary flex-1 justify-center" type="submit" data-testid="designer-lead-submit">
                    {leadSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send my quote request <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <a href={`tel:${BUSINESS.phoneTel}`} className="btn-secondary"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
                </div>
              </form>
            </div>
          )}

          {/* STAGE: Done */}
          {stage === "done" && (
            <div className="max-w-xl mx-auto text-center py-12" data-testid="designer-done">
              <CheckCircle2 className="w-14 h-14 mx-auto text-brand" />
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-6">Thanks, {lead.name.split(" ")[0]}.</h2>
              <p className="text-ink-soft mt-4 leading-relaxed">Maciej will personally reach out within 1 business day. Your render is saved and attached to your request - we will use it as the starting point for your in-person estimate.</p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <a href={`tel:${BUSINESS.phoneTel}`} className="btn-primary"><Phone className="w-4 h-4" /> Call now: {BUSINESS.phone}</a>
                <button onClick={resetAll} className="btn-secondary" data-testid="designer-new-design">Design another roof</button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* RELATED TOOLS */}
      <section className="py-16 bg-bg-soft border-y border-ink-line">
        <div className="container-x">
          <SectionHeading overline="More planning tools" title="Got a budget question or want a real inspection?" />
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            <Link to="/roof-cost-calculator" className="bg-white border border-ink-line p-6 hover:border-brand transition-all">
              <div className="font-heading text-lg font-extrabold">Roof Cost Calculator</div>
              <div className="text-sm text-ink-soft mt-2">60-second instant range for your home size + material.</div>
              <div className="text-brand text-sm font-bold mt-3 inline-flex items-center gap-1">Open calculator <ArrowRight className="w-4 h-4" /></div>
            </Link>
            <Link to="/free-roof-inspection" className="bg-white border border-ink-line p-6 hover:border-brand transition-all">
              <div className="font-heading text-lg font-extrabold">Free Roof Inspection</div>
              <div className="text-sm text-ink-soft mt-2">Walk-the-roof, photo report, written estimate. No obligation.</div>
              <div className="text-brand text-sm font-bold mt-3 inline-flex items-center gap-1">Book inspection <ArrowRight className="w-4 h-4" /></div>
            </Link>
            <Link to="/blog/roof-cost-oakland-county-mi-2026" className="bg-white border border-ink-line p-6 hover:border-brand transition-all">
              <div className="font-heading text-lg font-extrabold">2026 Cost Guide</div>
              <div className="text-sm text-ink-soft mt-2">Full pricing breakdown by home size and material in Oakland County.</div>
              <div className="text-brand text-sm font-bold mt-3 inline-flex items-center gap-1">Read guide <ArrowRight className="w-4 h-4" /></div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container-x max-w-3xl">
          <SectionHeading overline="Roof Designer FAQ" title="Common questions before you upload." />
          <div className="mt-8 border-t border-ink-line">
            {FAQS.map((f, i) => (
              <details key={i} open className="group border-b border-ink-line py-5">
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <h3 className="font-heading text-lg md:text-xl font-bold leading-snug text-ink group-open:text-brand transition-colors">{f.q}</h3>
                </summary>
                <p className="mt-3 text-ink-soft leading-relaxed text-[16px]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

// ---------- Engagement sub-component ----------
// Live countdown + rotating engagement copy so the homeowner stays put while
// Gemini Nano Banana works (typical render: 18-35s).  Once we exceed the
// expected window we degrade gracefully to "almost done" + indefinite spinner
// rather than implying we're stuck.
const RENDER_TIPS = [
  { t: 1,  msg: "Reading your photo..." },
  { t: 4,  msg: "Locating the roof region in your house." },
  { t: 9,  msg: "Choosing the right shingle texture and granule pattern." },
  { t: 15, msg: "Matching lighting, shadows, and time of day." },
  { t: 22, msg: "Painting your new roof color onto the surface." },
  { t: 28, msg: "Adding final detail to ridges, valleys, and edges." },
  { t: 34, msg: "Almost there - quality check..." },
];

function RenderingStage({ elapsed, material, color }) {
  const EXPECTED = 32; // seconds
  const pct = Math.min(95, Math.round((elapsed / EXPECTED) * 100));
  const tip = RENDER_TIPS.slice().reverse().find((x) => elapsed >= x.t) || RENDER_TIPS[0];
  const overtime = elapsed > EXPECTED;

  return (
    <div className="max-w-2xl mx-auto py-10 md:py-16" data-testid="designer-rendering">
      <div className="text-center">
        <div className="relative inline-flex items-center justify-center w-24 h-24">
          <Loader2 className="w-24 h-24 animate-spin text-brand/20" />
          <div className="absolute inset-0 flex items-center justify-center font-heading text-3xl font-extrabold text-ink tabular-nums" data-testid="designer-elapsed">
            {elapsed}s
          </div>
        </div>
        <div className="font-heading text-2xl md:text-3xl font-extrabold mt-6 leading-tight">
          {overtime ? "Almost done..." : "Generating your preview"}
        </div>
        <p className="text-ink-soft mt-2 max-w-md mx-auto leading-relaxed text-sm md:text-base">
          {material.label} in <strong className="text-ink">{color.label}</strong>
        </p>
      </div>

      {/* Progress bar */}
      <div className="mt-8 max-w-md mx-auto">
        <div className="h-2 bg-bg-soft border border-ink-line overflow-hidden">
          <div
            className="h-full bg-brand transition-all duration-1000 ease-linear"
            style={{ width: `${pct}%` }}
            data-testid="designer-progress"
          />
        </div>
        <div className="flex justify-between text-[11px] uppercase tracking-widest text-ink-soft mt-2 font-bold">
          <span>{pct}%</span>
          <span>{overtime ? "Slower than usual" : `~${Math.max(0, EXPECTED - elapsed)}s remaining`}</span>
        </div>
      </div>

      {/* Rotating engagement tip */}
      <div className="mt-8 bg-ink text-white p-5 md:p-6 max-w-md mx-auto border-l-4 border-brand" key={tip.t} data-testid="designer-tip">
        <div className="overline text-brand mb-1.5 text-[10px]">What&apos;s happening</div>
        <div className="font-heading text-base md:text-lg font-bold leading-snug">{tip.msg}</div>
      </div>

      {/* Engagement: did you know facts to keep the user reading */}
      <div className="mt-10 max-w-lg mx-auto text-center">
        <div className="overline text-ink-soft text-[10px]">While you wait</div>
        <p className="text-sm md:text-base text-ink leading-relaxed mt-2">
          Did you know? Most Oakland County homeowners pick <strong className="text-ink">architectural asphalt in Charcoal</strong> - it sits on roughly 60% of the homes Goral has installed since 2010.
        </p>
      </div>
    </div>
  );
}

